import React, {
  FunctionComponent,
  useCallback,
  useState,
  useRef,
  useLayoutEffect,
  useEffect,
} from "react";
import { LngLatBoundsLike } from "mapbox-gl";
import { useDebouncedCallback } from "use-debounce";

import { useGeocoding, GeocodeResponse } from "./use-geocoding";
import useTargetEffects from "@/use-target-effects";
import useSelectionEffects from "@/use-selection-effects";
import useIndicationEffects from "@/use-indication-effects";
import useKeyPress from "@/use-key-press";
import useGtagEffects from "@/use-gtag-effects";
import useRefinementEffects from "@/use-refinement-effects";
import { Position } from "@/types";
import LocationRequester from "./location-requester";

const Geolocator: FunctionComponent<GeolocatorProps> = ({
  onDone,
  className,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [features, setFeatures] = useState<GeocodeResponse | null>(null);
  const [query, setQuery] = useState("");
  const [index, setIndex] = useState(-1);

  const autoselectFirst = useRef(false);
  const input = useRef<HTMLInputElement>(null);

  // Application State
  const { setSelection } = useSelectionEffects();
  const { setIndication } = useIndicationEffects();
  const { service } = useGeocoding();
  const { setViewport } = useTargetEffects();

  const { triggerEvent, triggerCustomEvent } = useGtagEffects();
  const { setGeocodeContext } = useRefinementEffects();

  useLayoutEffect(() => {
    // Reset any selection state
    setSelection(null);
    setIndication(null);
  }, [input, setSelection, setIndication]);

  useKeyPress("ArrowDown", "keydown", () => setIndex((i) => i + 1));
  useKeyPress("ArrowUp", "keydown", () => setIndex((i) => i - 1));

  const onSelectFeatureHandler = useCallback(
    (feature: GeocodeResponse["features"][0]) => {
      const { bbox } = feature;

      if (bbox != null) {
        if (feature.center != null) {
          setGeocodeContext(feature.text, feature.center as [number, number]);
        }

        setViewport(bbox as LngLatBoundsLike);
        triggerCustomEvent("fuel_finder_find", {
          event_category: "Geolocator",
          event_label: "Find Ampol",
          ui_trigger: "search_result",
        });
        onDone();
      } else {
        console.warn("There is no bounding box for this feature");
      }
    },
    [setViewport, onDone, setGeocodeContext, triggerCustomEvent]
  );

  const onLocationHandler = useCallback(
    (position: Position) => {
      setGeocodeContext("My Location", position);

      // Create a bbox around the location
      triggerCustomEvent("fuel_finder_find", {
        event_category: "Geolocator",
        event_label: "Find Ampol",
        ui_trigger: "user_location",
      });
      setViewport(position);
      onDone();
    },
    [setViewport, onDone, setGeocodeContext, triggerCustomEvent]
  );

  useEffect(() => {
    if (autoselectFirst.current === true) {
      autoselectFirst.current = false;
      if (features != null && features.features.length > 0) {
        onSelectFeatureHandler(features.features[0]);
      }
    }
  }, [features, onSelectFeatureHandler]);

  useEffect(() => {
    let mounted = true;
    const safe = (fn: Function, ...args: any[]) => {
      if (mounted === true) {
        fn(...args);
      }
    };
    if (query.length < 3) return;

    const invoked = async () => {
      try {
        safe(setIsLoading, true);
        safe(setFeatures, null);
        triggerEvent("search", { search_term: query });

        const response = await service
          .forwardGeocode({
            query,
            limit: 3,
            countries: ["au"],
            types: [],
          })
          .send();
        const { body } = response;
        safe(setFeatures, body);
        safe(setIndex, -1);
      } catch (error) {
        console.log(error);
      } finally {
        safe(setIsLoading, false);
      }
    };

    invoked();
    return () => {
      mounted = false;
    };
  }, [query, service, triggerEvent]);

  useEffect(() => {
    if (features != null && features.features.length > 0) {
      triggerEvent("view_search_results", {});
    }
  }, [features, triggerEvent]);

  const queryDebounced = useDebouncedCallback(async (value: string) => {
    setQuery(value);
  }, 300);

  const onLocationChangeHandler = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) =>
      queryDebounced(event.target.value),
    [queryDebounced]
  );

  const onSubmitHandler = useCallback(
    (e?: React.FormEvent) => {
      e?.preventDefault();
      // Ready to process
      if (features != null && features.features.length > 0) {
        if (index >= 0) {
          onSelectFeatureHandler(features.features[index]);
        } else {
          onSelectFeatureHandler(features.features[0]);
        }
      }
      // If no features, then we will autoselect the first feature once
      // async search has completed
      autoselectFirst.current = true;
    },
    [features, onSelectFeatureHandler, index]
  );

  useKeyPress("Enter", "keydown", () => onSubmitHandler());

  return (
    <div
      className={`tw-bg-white tw-p-6 tw-bg-slate-50 tw-flex tw-flex-col tw-space-y-4 tw-rounded-xl tw-border-2 tw-border-nrma-dark-blue ${className}`}
    >
      <div className="tw-flex tw-flex-col tw-items-center">
        <div className="tw-w-max">
          <img
            src="https://cdn.alpacamaps.com/nrma/ampol-member-benefits/nrma_logo_rgb_dark-blue-600w.png"
            alt="NRMA Ampol Logo"
            className="tw-w-36 tw-pb-2"
          />
        </div>
        <div className="tw-text-center">
          <h1 className="tw-text-xl sm:tw-text-2xl @sm/main:tw-text-2xl tw-font-header tw-text-gray-800">
            Find Ampol Foodary Locations
          </h1>
        </div>
      </div>
      <div>
        <form
          onSubmit={onSubmitHandler}
          autoComplete="false"
          autoCorrect="false"
          className="tw-mt-2"
        >
          <label
            htmlFor="geocode"
            className="tw-font-semibold tw-text-gray-600"
          >
            Enter your Postcode or Suburb
          </label>
          <div className="tw-flex tw-flex-row tw-mt-1">
            <div className="tw-flex tw-mr-2 tw-relative">
              <input
                type="text"
                className="tw-border tw-rounded-lg tw-p-1 tw-w-full"
                onChange={onLocationChangeHandler}
                ref={input}
                id="geocode"
                data-testid="geocode-input"
                autoComplete="false"
                autoCorrect="false"
              />
              {isLoading && (
                <div className="tw-absolute tw-right-0 tw-inset-y-1.5">
                  <svg
                    className="tw-animate-spin -tw-ml-1 tw-mr-3 tw-h-5 tw-w-5 tw-text-nrma-dark-blue"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="tw-opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="tw-opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                </div>
              )}
            </div>
            <div className="tw-grow">
              <button
                className="tw-border tw-rounded-full tw-p-1 tw-px-3 tw-w-max tw-bg-nrma-dark-blue tw-text-white tw-font-button"
                type="submit"
              >
                Search
              </button>
            </div>
          </div>
          {features != null && (
            <div className="tw-flex-col tw-absolute">
              {features?.features.map((feature, i) => (
                <button
                  onClick={() => onSelectFeatureHandler(feature)}
                  key={feature.id}
                  data-testid="geocode-result text-nowrap"
                  className={` hover:tw-bg-sky-700 tw-w-80 tw-flex tw-p-2 tw-text-left ${
                    i === index ? "tw-bg-sky-700" : "tw-bg-slate-300"
                  }`}
                >
                  {feature.place_name}
                </button>
              ))}
            </div>
          )}
          <div className="tw-flex tw-flex-row">
            <LocationRequester onLocation={onLocationHandler} />
          </div>
        </form>
      </div>
    </div>
  );
};

export type GeolocatorProps = {
  onDone: VoidFunction;
  className?: string;
};

export default React.memo(Geolocator);
